import React from 'react';
import { MapPin, Users, DollarSign, CheckCircle2, Clock } from 'lucide-react';

interface CompactProjectCardProps {
  project: any;
  onViewDetails: (project: any) => void;
}

const CompactProjectCard: React.FC<CompactProjectCardProps> = ({ project, onViewDetails }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-primary-green/10 text-primary-green border border-primary-green/20';
      case 'active': return 'bg-primary-blue/10 text-primary-blue border border-primary-blue/20';
      case 'fundraising': return 'bg-primary-yellow/10 text-primary-blue border border-primary-yellow/20';
      default: return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle2 size={14} />;
      case 'active': return <Clock size={14} />;
      default: return <DollarSign size={14} />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Réalisé';
      case 'active': return 'En cours';
      case 'fundraising': return 'Collecte';
      default: return 'En attente';
    }
  };

  return (
    <div className="group card-udb p-6 hover:shadow-udb-lg hover:scale-105 transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-primary-blue/30"
         onClick={() => onViewDetails(project)}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900 text-base line-clamp-2 leading-tight mb-3 group-hover:text-primary-blue transition-colors">
            {project.title}
          </h3>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium ${getStatusColor(project.status)}`}>
              {getStatusIcon(project.status)}
              {getStatusText(project.status)}
            </span>
            {project.featured && (
              <span className="bg-primary-yellow/20 text-primary-blue text-xs px-2 py-1 font-medium flex items-center gap-1">
                <span>⭐</span>
                Vedette
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Location and beneficiaries */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MapPin size={14} className="text-primary-green flex-shrink-0" />
          <span className="truncate font-medium">{project.location}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users size={14} className="text-primary-blue flex-shrink-0" />
          <span className="truncate">{project.supporters} bénéficiaires</span>
        </div>
      </div>

      {/* Progress bars */}
      <div className="space-y-2 mb-3">
        {/* Funding progress */}
        <div>
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span className="font-medium">Financement</span>
            <span className="font-semibold text-primary-blue">{project.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 h-2 overflow-hidden">
            <div
              className={`h-2 transition-all duration-500 ${
                project.status === 'completed' ? 'bg-primary-green' : 'bg-primary-blue'
              }`}
              style={{ width: `${Math.min(project.progress, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Amount and action */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="text-sm">
          <span className="font-semibold text-primary-blue">
            {(project.currentAmount / 1000000).toFixed(1)}M
          </span>
          <span className="text-gray-500">
            /{(project.targetAmount / 1000000).toFixed(0)}M FCFA
          </span>
        </div>
        <button
          className={`px-4 py-2 text-sm font-medium transition-all transform hover:scale-105 ${
            project.status === 'completed'
              ? 'bg-primary-green/10 text-primary-green border border-primary-green/20 hover:bg-primary-green hover:text-white'
              : 'bg-primary-blue/10 text-primary-blue border border-primary-blue/20 hover:bg-primary-blue hover:text-white'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails(project);
          }}
        >
          {project.status === 'completed' ? 'Voir résultats' : 'Voir détails'}
        </button>
      </div>
    </div>
  );
};

export default CompactProjectCard;